import {Component, Inject, OnInit} from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {ManagedSubscriber, User} from '@nh/angular-tses';
import {DepartureCommentType} from 'app/comment/departure-comment-type';
import {EnvironmentVariableService} from 'app/core/services/environment-variable.service';
import {ErrorMessageMap, ErrorMessageTypes, ErrorMessages} from 'app/core/validation/error-messages';
import {PermissionStateDiffer} from 'app/departures/departure/diary-view/diary-event-card/diary-permission-card/permission-state-differ';
import {DepartureReasonCodes, ReasonCodeService} from 'app/diary/reason-codes/reason-codes.service';
import {DepartureRole} from 'app/permissions/types';
import {AssignableRole} from 'app/transition/modals/user-assign-modal/user-assign-modal.component';
import {DepartureComment} from '../../../comment/departure-comment';
import {Reassignment, RoleService} from '../../../core/services/role.service';
import {FormValidator} from '../../../core/validation/form-validator';
import {DiarySnapshot} from '../../../diary/diary-snapshot';
import {DiaryService} from '../../../diary/diary.service';
import {DepartureProperties, createDeparturePropertiesChange} from '../../../diary/properties/departure-properties';
import {DeparturePermissions, reassign} from '../../../permissions/departure-permissions';
import {toArray} from '../../../utils/array-utils';
import {TransitionInputData} from '../transition-input-data';
import {TransitionOutputData} from '../transition-output-data';

@Component({
  selector: 'das-base-transition-modal',
  templateUrl: './base-transition-modal.component.html',
  styleUrls: ['./base-transition-modal.component.scss'],
})
export class BaseTransitionModalComponent extends ManagedSubscriber implements OnInit {
  public descriptionParagraphs: string[] = [];
  public reasonCodes: string[] = [];
  public isRecommendation: boolean = false;

  public inputComment: UntypedFormControl;

  /** Map from DepartureRoles (as string) to currently assigned user */
  public currentUsers: Record<string, User> = {};

  protected latestProperties: DepartureProperties;
  protected latestPermissions: DeparturePermissions;
  protected readonly _differ: PermissionStateDiffer = new PermissionStateDiffer();
  protected errorMessages: ErrorMessageMap = {
    [ErrorMessageTypes.REQUIRED]: 'A comment is mandatory to progress this departure',
    [ErrorMessageTypes.WHITESPACE]: 'A comment must not contain only white space',
  };

  constructor(
    public dialogRef: MatDialogRef<BaseTransitionModalComponent, TransitionOutputData>,
    @Inject(MAT_DIALOG_DATA) public data: TransitionInputData,
    protected roleService: RoleService,
    protected diaryService: DiaryService,
    protected reasonCodeService: ReasonCodeService,
    protected environmentVariableService: EnvironmentVariableService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.descriptionParagraphs = toArray(this.data.description);
    this.setupFormControls();
    this.subscriptions.push(
      this.diaryService.onDiarySnapshotChange().subscribe((snapshot: DiarySnapshot) => {
        this.latestPermissions = snapshot.permissions;
        this.latestProperties = snapshot.properties;
        if (this.data.assignableRoles && this.data.assignableRoles.length) {
          const rolesToGet: DepartureRole[] = this.data.assignableRoles.map((r) => r.role);
          this.roleService
            .getCurrentAssignees(snapshot.permissions, rolesToGet)
            .then((currentAssignees: AssignableRole[]) => {
              currentAssignees.forEach(
                (assignment: AssignableRole) => (this.currentUsers[assignment.role] = assignment.users[0])
              );
            });
        }
      })
    );
  }

  protected setupFormControls(): void {
    this.inputComment = new UntypedFormControl(
      '',
      this.data.commentRequired ? [Validators.required, FormValidator.whitespace] : [FormValidator.whitespace]
    );
  }

  protected generateDiarySnapshot(
    inputComment: string,
    commentType: DepartureCommentType,
    reassignments: Reassignment[] = [],
    propertiesRecord: Record<string, string | number | boolean>,
    reasonCodes?: string[]
  ): DiarySnapshot {
    let snapShotComment: DepartureComment;
    let snapShotPermissions: DeparturePermissions;
    let snapShotProperties: DepartureProperties;
    let snapShotReasonCodes: DepartureReasonCodes;

    if (inputComment && commentType) {
      snapShotComment = {
        commentText: inputComment,
        departureId: this.data.departureId.value,
        mentionedUsers: [],
        commentType: commentType,
      };
    }
    if (reassignments.length) {
      snapShotPermissions = reassign(reassignments, this.latestPermissions);
    }
    if (this.data.roleToAssign) {
      snapShotPermissions = this.setAssignee(snapShotPermissions);
    }
    if (this.data.assignToDasAdmin) {
      snapShotPermissions = this.setAssigneeWithId(
        snapShotPermissions,
        this.environmentVariableService.getConfig().dasAdminUserId
      );
    }
    if (!this._differ.diff(snapShotPermissions, this.latestPermissions).length) {
      snapShotPermissions = null;
    }
    if (reasonCodes && reasonCodes.length) {
      snapShotReasonCodes = {
        departureId: this.data.departureId.value,
        reasonCodes: reasonCodes,
        recommendation: this.isRecommendation,
      };
    }

    if (!this.isEmpty(propertiesRecord)) {
      snapShotProperties = createDeparturePropertiesChange(
        this.latestProperties,
        propertiesRecord,
        this.data.departureId.value
      );
    }

    return {
      comment: snapShotComment,
      permissions: snapShotPermissions,
      properties: snapShotProperties,
      reasonCodes: snapShotReasonCodes,
    };
  }

  private isEmpty(obj: Object): boolean {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  }

  public actionButtonText(): string {
    return this.data && this.data.actionButton ? this.data.actionButton : 'CONFIRM & CLOSE';
  }

  public confirmAndClose(): void {
    const returnData: TransitionOutputData = {
      snapShot: this.generateDiarySnapshot(
        this.inputComment.value,
        DepartureCommentType.COMMENT,
        [],
        {},
        this.reasonCodes
      ),
      cancelled: false,
    };
    this.dialogRef.close(returnData);
  }

  public cancel(): void {
    this.dialogRef.close({
      cancelled: true,
      snapShot: {},
    });
  }

  private setAssignee(permissions: DeparturePermissions): DeparturePermissions {
    if (!permissions) {
      permissions = Object.assign({}, this.latestPermissions);
    }

    let userId: string | string[] = permissions[this.data.roleToAssign];
    if (userId instanceof Array) {
      userId = userId[0];
    }

    permissions.assignee = userId;

    delete permissions.id;
    delete permissions.createdBy;
    delete permissions.createdAt;
    return permissions;
  }

  private setAssigneeWithId(permissions: DeparturePermissions, userId: string): DeparturePermissions {
    if (!permissions) {
      permissions = Object.assign({}, this.latestPermissions);
    }

    permissions.assignee = userId;

    delete permissions.id;
    delete permissions.createdBy;
    delete permissions.createdAt;
    return permissions;
  }

  /**
   * Returns true if any of the given roles are already assigned
   *
   * @param departureRoles {DepartureRole[]}   Departure roles to check
   * @returns              {boolean}           True if any of the given roles are assigned
   */
  protected _rolesAssigned(...departureRoles: DepartureRole[]): boolean {
    return departureRoles.every((role: DepartureRole) => !!this.currentUsers[role]);
  }

  public hasWarning(): boolean {
    return !!this.data && !!this.data.warning;
  }

  public errorText(): string {
    return ErrorMessages.fromControl(this.inputComment, this.errorMessages)[0] || '';
  }
}
